import React from "react";
import ReactMarkdown from "react-markdown/with-html";
import { Link } from "gatsby";

import { IBasicText } from "../../../interfaces";
import { guid, parameterize } from "../../../utils";
import { scriptType } from "../../../md-custom-type-plugins";
import { fillTemplateStrings, parseAdditionalRules } from "./helpers/markdown-utils";

interface ElementClasses {
  a?: string;
  p?: string;
  h1?: string;
  h2?: string;
  h3?: string;
  h4?: string;
  h5?: string;
  h6?: string;
}

const defaultElementClasses = {
  a: "",
  p: "",
  h1: "",
  h2: "",
  h3: "",
  h4: "",
  h5: "",
  h6: ""
};

interface MdProps {
  content: IBasicText;
  elementClasses?: ElementClasses;
  //pass an array of objects that match template strings in markdown
  //allows you to still control the placement in the cms while grabbing data from an api
  bindValues?: object;
  namespace?: string;
  className?: string;
}

class CmsMarkdown extends React.Component<MdProps, {}> {
  mdProps: MdProps;
  parseHtml: any;
  scriptStrings: string[] = [];
  scriptElements: HTMLScriptElement[];

  constructor(mdProps) {
    super(mdProps);
    const { content, elementClasses, bindValues, namespace, className } = mdProps;
    this.mdProps = {
      namespace,
      content,
      bindValues,
      elementClasses: { ...defaultElementClasses, ...elementClasses },
      className
    };
  }

  Link = ({ children, href }) => {
    const eleProps = {
      href: fillTemplateStrings(href, this.mdProps.bindValues),
      className: `${this.mdProps.elementClasses["a"]}`
    };

    const { href: filledHref, className } = eleProps;

    if (filledHref[0] === "/" || filledHref[0] === "#") {
      return (
        <Link to={`${filledHref}`} className={className}>
          {children}
        </Link>
      );
    } else {
      return (
        <a
          href={filledHref != null ? filledHref : "#"}
          target="_blank'"
          rel="noreferrer noopener"
          className={className}
        >
          {children}
        </a>
      );
    }
  };

  Paragraph = ({ children }) => {
    return <p className={`${this.mdProps.elementClasses["p"]}`}>{children}</p>;
  };

  Text = ({ value }) => {
    const text = fillTemplateStrings(value, this.mdProps.bindValues);
    const parsedTextElement = parseAdditionalRules(text);

    return parsedTextElement;
  };

  Heading = ({ level, children }) => {
    const eleProps = {
      className: `${this.mdProps.elementClasses[`h${level}`]}`,
      id: parameterize(children[0]?.props?.value)
    };

    switch (level) {
      case 1: {
        return <h1 {...eleProps}>{children}</h1>;
      }
      case 2: {
        return <h2 {...eleProps}>{children}</h2>;
      }
      case 3: {
        return <h3 {...eleProps}>{children}</h3>;
      }
      case 4: {
        return <h4 {...eleProps}>{children}</h4>;
      }
      case 5: {
        return <h5 {...eleProps}>{children}</h5>;
      }
      case 6: {
        return <h6 {...eleProps}>{children}</h6>;
      }
      default:
        return <div></div>;
    }
  };

  createScriptElement(scriptNode) {
    const scriptElement = document.createElement("script");

    Array.prototype.slice.call(scriptNode.attributes).forEach((item) => {
      //parse only the attributes from the attributes object
      //a normal loop will iterate over the properties as well
      scriptElement.setAttribute(item.name, item.value);
    });

    scriptElement.setAttribute("async", "false");
    scriptElement.setAttribute("defer", "false");

    if (scriptNode.childNodes.length > 0) {
      let code = scriptNode.childNodes[0].nodeValue;

      if (code.includes("MktoForms2")) {
        code = `
          var mktoInterval = setInterval(function() {
            if(typeof MktoForms2 !== undefined) {
              ${code}
              try {
                MktoForms2.whenReady(function (form) {
                  if(typeof ppcUrlCookiePart2 !== "undefined") ppcUrlCookiePart2()
                })
                MktoForms2.onFormRender(function (form) {
                  Array.from(document.querySelectorAll('.mktoCheckboxList')).forEach((el) => {

                    if (el.children.length > 2) {
                      el.closest('.mktoFormRow').classList.add('mktoCheckboxesListContainer')
                    } else {
                      el.closest('.mktoFormRow').classList.add('mktoCheckboxListContainer')
                    }
                  })
                })
              } catch (err) {
                if(typeof ppcUrlCookiePart2 !== "undefined") ppcUrlCookiePart2()
              }
              clearInterval(mktoInterval);
            }
          }, 500);
        `;
      }
      try {
        scriptElement.appendChild(document.createTextNode(code));
      } catch (error) {
        scriptElement.text = code;
      }
    }

    return scriptElement;
  }

  handleLoad = (res: () => void = () => {}) => {
    return () => {
      res();
    };
  };

  // We need to wait for the script tag to load the library then we can run the code
  async sortAndLoadScriptTags(scriptTags: HTMLScriptElement[]): Promise<any> {
    const loadPromises = [];
    const runScripts = [];

    scriptTags.forEach((tag) => {
      const scriptElement = this.createScriptElement(tag);
      if (scriptElement.childNodes.length === 0) {
        // const loadPromise = new Promise((res, rej) => {
        //   scriptElement.addEventListener("onLoad", res)
        // })
        // loadPromises.push(loadPromise)
        loadPromises.push(scriptElement.src);
        document.body.appendChild(scriptElement);
      } else {
        runScripts.push(scriptElement);
      }
    });

    await Promise.all(loadPromises);

    runScripts.forEach((script) => {
      document.body.appendChild(script);
    });
  }

  parseScriptStringToElement(eleStrings: string[]): HTMLScriptElement[] {
    let elements = [];

    eleStrings.forEach((ele) => {
      const div = document.createElement("div");
      div.innerHTML = ele.trim();
      const scriptEle = div.firstElementChild as HTMLScriptElement;

      elements.push(scriptEle);
    });

    return elements;
  }

  parseMarkdown(text) {
    const props = {
      source: text,
      plugins: [scriptType],
      key: guid(),
      escapeHtml: false,
      renderers: {
        paragraph: this.Paragraph,
        heading: this.Heading,
        link: this.Link,
        text: this.Text,
        script: (scriptContent) => {
          this.scriptStrings.push(scriptContent.value);
          return null;
        },
        parsedHtml: (html) => {
          return html.element;
        }
      }
    };

    return <ReactMarkdown {...props} />; // eslint-disable-line no-eval
  }

  async initScripts() {
    this.scriptElements = this.parseScriptStringToElement(this.scriptStrings);
    if (this.scriptElements.length > 0) {
      await this.sortAndLoadScriptTags(this.scriptElements);
    }
  }

  async componentDidMount() {
    this.initScripts();
  }

  async componentWillUpdate() {
    this.initScripts();
  }

  componentWillUnmount() {
    this.scriptElements.forEach((ele) => {
      ele.removeEventListener("onLoad", this.handleLoad());
      try {
        document.body.removeChild(ele);
      } catch (error) {
        console.warn(ele, error);
      }
    });
  }

  render() {
    if (this.mdProps.content == null) {
      return <div></div>;
    }
    const {
      content: { align_text, component, id, text },
      className,
      namespace
    } = this.mdProps;

    return (
      <div
        style={{ textAlign: align_text }}
        className={`cms-markdown cms-${component} ${component}-${id} width-full ${
          className != undefined ? className : ""
        } ${namespace != undefined ? namespace : ""}`}
      >
        {this.parseMarkdown(text)}
      </div>
    );
  }
}

export default CmsMarkdown;
